import React, { ReactNode, useState } from 'react';
import { ModalsKeys, TabsOption } from 'global';
import { useQuery } from '@apollo/client';
import { useModal } from 'hooks/useModal';
import { CircularProgress, Stack } from '@mui/material';
import { TabsContainer } from 'components/shared/TabsContainer/TabsContainer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';

import { DealSheetRuleForm } from './rule';
import { getInitialValues } from './rule/lib';
import { PageViewType } from './table/types';
import { dealSheetConfigurationApi } from '../api';
import { DealSheetConfigurationViewModel } from '../types';
import { GlobalRulesTableContainer, RulesTableContainer } from './table';

export const DealSheet = () => {
  const { handleOpen } = useModal(ModalsKeys.DealSheetRuleDetailsDialog);

  const [pageView, setPageView] = useState<{ type: PageViewType; id: string | null }>({
    type: 'tabs',
    id: null,
  });

  const {
    data,
    error,
    loading: isConfigLoading,
  } = useQuery<{ dealSheets: { result: { entity: DealSheetConfigurationViewModel } } }>(
    dealSheetConfigurationApi.getConfig,
    {
      skip: !pageView.id,
      notifyOnNetworkStatusChange: true,
      variables: { input: { id: pageView.id! } },
    }
  );

  const config = data?.dealSheets?.result?.entity ?? null;

  const handleChangeView = (type: PageViewType, id: string | null) => {
    if (type === 'details' && id) {
      handleOpen({ configId: id, document: dealSheetConfigurationApi.getConfig });
      return;
    }

    setPageView({ type, id });
  };

  const tabs: TabsOption[] = [
    {
      value: 'rulesList',
      label: 'Business Rule',
      component: <RulesTableContainer onChangeView={handleChangeView} />,
    },
    {
      label: 'Global Rules',
      value: 'globalRulesList',
      component: <GlobalRulesTableContainer />,
    },
  ];

  const render: Record<Exclude<PageViewType, 'details'>, ReactNode> = {
    tabs: <TabsContainer tabs={tabs} />,
    create: (
      <DealSheetRuleForm
        id={pageView.id}
        isConfigLoading={isConfigLoading}
        initialValues={getInitialValues(null)}
        onCancel={() => setPageView({ type: 'tabs', id: null })}
      />
    ),
    update: (
      <RenderWithFallback
        error={error}
        isLoading={isConfigLoading}
        fallBack={
          <Stack alignItems="center" justifyContent="center" height="100%" width="100%">
            <CircularProgress />
          </Stack>
        }
      >
        <DealSheetRuleForm
          id={pageView.id}
          isConfigLoading={isConfigLoading}
          initialValues={getInitialValues(config)}
          onCancel={() => setPageView({ type: 'tabs', id: null })}
        />
      </RenderWithFallback>
    ),
  };

  return <>{render[pageView.type as Exclude<PageViewType, 'details'>]}</>;
};
